import { useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  ChevronDown,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  ShieldCheck,
} from "lucide-react";

type Severity = "critical" | "negotiate" | "standard";

const FLAGS: {
  id: string;
  severity: Severity;
  section: string;
  title: string;
  excerpt: string;
  explanation: string;
}[] = [
  {
    id: "f1",
    severity: "critical",
    section: "§4.2",
    title: "Termination without notice",
    excerpt:
      "Lessor reserves the right to terminate this Agreement at any time without prior notice to the Lessee.",
    explanation:
      "California law requires 30 or 60 days notice depending on tenancy length. This clause is likely unenforceable — but if you sign, the landlord may try to use it against you.",
  },
  {
    id: "f2",
    severity: "critical",
    section: "§7.1",
    title: "Waiver of jury trial",
    excerpt:
      "Tenant hereby waives all rights to a trial by jury in any dispute arising from this Agreement.",
    explanation:
      "Forfeits a fundamental legal protection. Any dispute would be decided by a single judge or arbitrator chosen in advance — typically less favorable to tenants.",
  },
  {
    id: "f3",
    severity: "negotiate",
    section: "§9.0",
    title: "Early termination penalty (2 months rent)",
    excerpt:
      "Early termination shall require 60 days written notice and a penalty equal to two (2) months rent.",
    explanation:
      "Steep but legal. California caps penalties at actual damages — push to reduce to one month or add a buyout clause that scales with time remaining.",
  },
  {
    id: "f4",
    severity: "negotiate",
    section: "§12.3",
    title: "Automatic renewal",
    excerpt:
      "This Agreement shall automatically renew for successive 12-month terms unless terminated by either party with 90 days notice.",
    explanation:
      "Auto-renewal with a long notice window is a common trap. Set a calendar reminder, or negotiate a month-to-month rollover instead.",
  },
  {
    id: "f5",
    severity: "standard",
    section: "§3.1",
    title: "Security deposit (1.5 months rent)",
    excerpt:
      "Tenant shall pay a security deposit equal to one and one-half months rent, refundable within 21 days of move-out.",
    explanation: "Standard for California. The 21-day return window matches state law.",
  },
];

const SEVERITY_STYLES: Record<
  Severity,
  {
    dot: string;
    bg: string;
    border: string;
    label: string;
    text: string;
    Icon: typeof AlertTriangle;
  }
> = {
  critical: {
    dot: "bg-rose-500",
    bg: "bg-rose-50/70",
    border: "border-rose-200",
    label: "Critical",
    text: "text-rose-700",
    Icon: AlertTriangle,
  },
  negotiate: {
    dot: "bg-amber-500",
    bg: "bg-amber-50/70",
    border: "border-amber-200",
    label: "Negotiate",
    text: "text-amber-700",
    Icon: AlertCircle,
  },
  standard: {
    dot: "bg-emerald-500",
    bg: "bg-emerald-50/70",
    border: "border-emerald-200",
    label: "Standard",
    text: "text-emerald-700",
    Icon: CheckCircle2,
  },
};

export function SampleReportPreview() {
  const [open, setOpen] = useState<Record<string, boolean>>({ f1: true });
  const [showStickyCta, setShowStickyCta] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  function toggle(id: string) {
    setOpen((s) => ({ ...s, [id]: !s[id] }));
  }

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    function onScroll() {
      if (!el) return;
      setShowStickyCta(el.scrollTop > 80);
    }
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  function scrollToAnalyzer() {
    document.getElementById("analyzer")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <div className="relative mx-auto w-full max-w-4xl">
      {/* Frame */}
      <div className="overflow-hidden rounded-2xl border border-border bg-white shadow-card">
        {/* Report header */}
        <div className="flex items-center justify-between gap-4 border-b border-border bg-gradient-to-br from-slate-50 to-white px-6 py-5">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              Sample analysis · Residential lease
            </div>
            <h3 className="mt-1 text-lg font-bold text-foreground">12-month lease report</h3>
          </div>
          <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2">
            <ShieldCheck className="h-5 w-5 text-amber-600" />
            <div className="leading-tight">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-amber-700">
                Risk Score
              </div>
              <div className="text-base font-bold text-amber-800">
                72<span className="text-xs font-medium text-amber-600">/100 · Moderate</span>
              </div>
            </div>
          </div>
        </div>

        {/* Severity legend */}
        <div className="flex flex-wrap items-center gap-4 border-b border-border bg-white px-6 py-3 text-xs">
          {(["critical", "negotiate", "standard"] as Severity[]).map((s) => {
            const cfg = SEVERITY_STYLES[s];
            return (
              <div key={s} className="flex items-center gap-2 text-muted-foreground">
                <span className={`h-2 w-2 rounded-full ${cfg.dot}`} />
                <span className="font-medium text-foreground">{cfg.label}</span>
              </div>
            );
          })}
          <div className="ml-auto text-[11px] text-muted-foreground">
            5 findings · scroll to expand
          </div>
        </div>

        {/* Scrollable report body */}
        <div className="relative">
          <div
            ref={scrollRef}
            className="hide-scrollbar relative max-h-[460px] overflow-y-auto px-6 py-5"
          >
            <ul className="space-y-3">
              {FLAGS.map((f) => {
                const cfg = SEVERITY_STYLES[f.severity];
                const isOpen = !!open[f.id];
                return (
                  <li
                    key={f.id}
                    className={`overflow-hidden rounded-xl border ${cfg.border} ${cfg.bg} transition-all`}
                  >
                    <button
                      type="button"
                      onClick={() => toggle(f.id)}
                      className="flex w-full items-start gap-3 px-4 py-3 text-left"
                      aria-expanded={isOpen}
                    >
                      <cfg.Icon className={`mt-0.5 h-4 w-4 shrink-0 ${cfg.text}`} />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            className={`text-[10px] font-bold uppercase tracking-wider ${cfg.text}`}
                          >
                            {cfg.label}
                          </span>
                          <span className="text-[11px] font-semibold text-muted-foreground">
                            {f.section}
                          </span>
                        </div>
                        <div className="mt-0.5 text-sm font-semibold text-foreground">
                          {f.title}
                        </div>
                      </div>
                      <ChevronDown
                        className={`mt-1 h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
                          isOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>
                    {isOpen && (
                      <div className="space-y-3 px-4 pb-4 animate-fade-in">
                        <blockquote
                          className={`border-l-2 ${cfg.border} bg-white/70 px-3 py-2 text-[12px] italic leading-relaxed text-muted-foreground`}
                        >
                          "{f.excerpt}"
                        </blockquote>
                        <p className="text-[13px] leading-relaxed text-foreground">
                          {f.explanation}
                        </p>
                      </div>
                    )}
                  </li>
                );
              })}

              {/* Locked teaser */}
              <li className="rounded-xl border border-dashed border-border bg-white/60 px-4 py-5 text-center">
                <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                  + 3 more findings
                </div>
                <div className="mt-1 text-sm text-foreground">
                  Unlock full report on your own document
                </div>
              </li>
            </ul>

            <div className="h-12" />
          </div>

          {/* Bottom fade */}
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white via-white/85 to-transparent"
            aria-hidden
          />

          {/* Sticky CTA inside preview */}
          <div
            className={`pointer-events-none absolute inset-x-0 bottom-3 flex justify-center transition-all duration-300 ${
              showStickyCta ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"
            }`}
          >
            <button
              type="button"
              onClick={scrollToAnalyzer}
              className="pointer-events-auto inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/30 transition-all hover:scale-105 hover:shadow-xl"
            >
              Analyze Your Contract
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <style>{`
        .hide-scrollbar::-webkit-scrollbar { width: 6px; }
        .hide-scrollbar::-webkit-scrollbar-thumb { background: rgba(15,23,42,0.15); border-radius: 999px; }
        .hide-scrollbar::-webkit-scrollbar-track { background: transparent; }
      `}</style>
    </div>
  );
}
